import React, { useEffect, useState, useRef, useMemo } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
  useReactFlow,
  ReactFlowProvider,
  Position,
} from 'reactflow';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Divider,
  Button,
  Menu,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
  Drawer,
  useTheme,
  useMediaQuery,
  Fab,
} from '@mui/material';
import 'reactflow/dist/style.css';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { ListItemIcon } from '@mui/material';

import {
  CameraAlt,
  Refresh,
  Visibility,
  Menu as MenuIcon,
  Close as CloseIcon,
  FilterList,
} from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';
import { useKnowledgeMapSubjects } from '../Sidebar/ConceptBuilderSideBar.tsx';

const LEVEL_GAP_X = 600;
const VERTICAL_GAP = 120;

// Import your actual services
import { AiActions } from '../../Services/_actions/Ai/ai.actions.ts';
import { useLocation, useNavigate } from 'react-router';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import SearchableDropdown from '../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { ConstantActions } from '../../Services/_actions/constant.actions.js';
import { globalpermissions } from '../../Services/Permission.js';
import { exportFlowToPdf } from '../Shared/FlowToPdf/exportFlowToPdf.tsx';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import { exportTableToPdf } from './exportTableToPdf.tsx';
import KnowledgeMapTable from './KnowledgeMapTable.tsx';
import TableViewIcon from '@mui/icons-material/TableView';

const topicColors = [
  '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
];

const mixColors = (color1, color2) => {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');

  const r1 = parseInt(hex1.substr(0, 2), 16);
  const g1 = parseInt(hex1.substr(2, 2), 16);
  const b1 = parseInt(hex1.substr(4, 2), 16);

  const r2 = parseInt(hex2.substr(0, 2), 16);
  const g2 = parseInt(hex2.substr(2, 2), 16);
  const b2 = parseInt(hex2.substr(4, 2), 16);

  const r = Math.round((r1 + r2) / 2);
  const g = Math.round((g1 + g2) / 2);
  const b = Math.round((b1 + b2) / 2);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const FlowComponent = () => {
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const location = useLocation();
  const constantActions = ConstantActions();
  const theme = useTheme();

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const useCompactZoom = isMobile || isDesktop;

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [allNodes, setAllNodes] = useState([]);
  const [allEdges, setAllEdges] = useState([]);
  const [curriculumData, setCurriculumData] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [sidePanelContent, setSidePanelContent] = useState(null);
  const [aiActions] = useState(AiActions());
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl1, setAnchorEl1] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dataToDelete, setDataToDelete] = useState(null);
  const [error, setError] = useState('');
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showFullView, setShowFullView] = useState(true);
  const loggedInUser = useRecoilValue(userInformationAtom);
  const loggedInUsername = loggedInUser?.username || '';
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [showTableView, setShowTableView] = useState(false);
  const tableRef = useRef(null);
  const isSuperUser = loggedInUser?.is_superuser === true ;
  const hasClassListPermission = isSuperUser || constantActions.userHasPermission([globalpermissions?.performancePermissions?.classListAccess]);

  // const isSuperUser = useMemo(() => {
  //   try {
  //     const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
  //     return userInfo?.is_superuser === true;
  //   } catch {
  //     return false;
  //   }
  // }, []);

  const {
    entityData: studentEntityData,
  } = useKnowledgeMapSubjects(true, undefined, { fetchKnowledgeMap: false });

  useEffect(() => {
    if (hasClassListPermission) return;// Superuser manually picks class
    if (!studentEntityData?.items?.length || !loggedInUsername) return;
    studentEntityData.items.forEach((item: any) => {
      if (item?.entity?.username?.toLowerCase() === loggedInUsername.toLowerCase()) {
        const studentClass = item.entity.class;
        if (studentClass) {
          setSelectedClass(String(studentClass));
        }
      }
    });
  }, [studentEntityData, loggedInUsername, isSuperUser]);
  // Mobile-specific states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);

  const levelSpacing = 110;
  const reactFlowWrapper = useRef(null);
  const { fitView } = useReactFlow();

  const handleMenuOpen = (event, nodeId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };


  const handleDownloadOpen = (event, nodeId) => {
    event.stopPropagation();
    setAnchorEl1(event.currentTarget);
  };

  const handleDownloadClose = () => {
    setAnchorEl1(null);
  };


  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (location.state?.selectedSubject) {
      setSelectedSubject(location.state.selectedSubject);
      setSelectedClass(location.state.selectedClass);
    }
  }, [location.state]);

  const handleEdit = () => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );
    if (currentData) {
      navigate(`/r/student/edit/${currentData._id}`, {
        state: { subjectData: currentData },
      });
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );
    setDataToDelete(currentData);
    setConfirmOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async (confirmed) => {
    if (!confirmed) {
      setConfirmOpen(false);
      return;
    }

    if (dataToDelete && dataToDelete._id) {
      try {
        await aiActions.deleteKnowledge(dataToDelete._id);
        setCurriculumData(curriculumData.filter((c) => c._id !== dataToDelete._id));
        setNodes([]);
        setEdges([]);
        setAllNodes([]);
        setAllEdges([]);
        setSelectedClass('');
        setSelectedSubject('');
        setSidePanelContent(null);
        showSnackBar('Deleted successfully!', 'success');
        setError('');
      } catch (error) {
        showSnackBar('Failed to delete. Please try again.', 'error');
        setError('Failed to delete. Please try again.');
      }
    }
    setConfirmOpen(false);
  };

  // Load curriculum data from API
  useEffect(() => {
       if (hasClassListPermission && !selectedOrganisation) return;
    const fetchData = async () => {
      try {
        const organisationId = hasClassListPermission ? selectedOrganisation?.organisation_id : undefined;
        const data = await aiActions.getKnowledgeList(organisationId);
        setCurriculumData(data);
        const classes = [...new Set(data.map((d) => d.class))].sort();
        setAvailableClasses(classes);
      } catch (err) {
        console.error(err);
        setError('Failed to load curriculum data');
      }
    };
    fetchData();
  }, [selectedOrganisation]);

  useEffect(() => {
    if (selectedClass) {
      const subjects = curriculumData
        .filter((d) => String(d.class) === String(selectedClass))
        .map((d) => d.subject);
      setAvailableSubjects([...new Set(subjects)]);
    } else {
      setAvailableSubjects([]);
    }
  }, [selectedClass, curriculumData]);

  // Build the flow when both class and subject are selected
  useEffect(() => {
    if (selectedClass && selectedSubject && curriculumData.length > 0) {
      buildFlow();
    } else {
      setNodes([]);
      setEdges([]);
      setAllNodes([]);
      setAllEdges([]);
      setSidePanelContent(null);
    }
  }, [selectedClass, selectedSubject, curriculumData]);

  const getSubtopicColor = (subtopicName, topicColorMap, subtopicParents) => {
    const parents = subtopicParents[subtopicName] || [];

    if (parents.length === 0) {
      return topicColorMap[0];
    } else if (parents.length === 1) {
      return topicColorMap[parents[0]];
    } else if (parents.length === 2) {
      return mixColors(topicColorMap[parents[0]], topicColorMap[parents[1]]);
    } else {
      return mixColors(topicColorMap[parents[0]], topicColorMap[parents[1]]);
    }
  };

  // Helper function to flatten nested prerequisites recursively
  const flattenPrerequisites = (prereqObj, parentId = null, level = 0) => {
    const result = [];

    if (prereqObj && typeof prereqObj === 'object' && prereqObj.name) {
      result.push({
        id: prereqObj.id,
        name: prereqObj.name,
        parentId: parentId,
        level: level,
        hasChildren: prereqObj.children && prereqObj.children.length > 0
      });

      // Recursively process children
      if (prereqObj.children && prereqObj.children.length > 0) {
        prereqObj.children.forEach(child => {
          result.push(...flattenPrerequisites(child, prereqObj.id, level + 1));
        });
      }
    }

    return result;
  };

  const estimateHeight = (label) => {
    const base = 40;
    const perLine = 18;
    const charsPerLine = 18;

    const lines = Math.ceil(label.length / charsPerLine);
    return base + (lines - 1) * perLine;
  };

  const relayoutFilteredNodes = (nodesToLayout, clickedNode) => {
    const layoutNodes = [...nodesToLayout];
    const subtopicNode = layoutNodes.find(n => n.id === clickedNode.id);

    if (!subtopicNode) return layoutNodes;

    // Position the clicked subtopic on the left
    const startX = 100;
    const startY = 300;
    subtopicNode.position = { x: startX, y: startY };

    // Separate prerequisites by level
    const prereqsByLevel = {};
    layoutNodes.forEach(n => {
      if (n.data.type === 'prerequisite') {
        const level = n.data.level || 0;
        if (!prereqsByLevel[level]) prereqsByLevel[level] = [];
        prereqsByLevel[level].push(n);
      }
    });

    // Layout prerequisites HORIZONTALLY to the right, grouped by level
    Object.keys(prereqsByLevel).forEach(levelKey => {
      const level = parseInt(levelKey);
      const prereqs = prereqsByLevel[level];
      const xOffset = startX + 350 + (level * 400);
      const totalHeight = (prereqs.length - 1) * 100;
      const startYForLevel = startY - totalHeight / 2;

      prereqs.forEach((prereq, idx) => {
        prereq.position = {
          x: xOffset,
          y: startYForLevel + idx * 100
        };
      });
    });

    return layoutNodes;
  };

  const relayoutAllNodes = (nodesToLayout) => {
    const layoutNodes = [...nodesToLayout];

    // Group nodes by type
    const subtopics = layoutNodes.filter(n => n.data.type === 'subtopic');
    const prereqsByLevel = {};

    layoutNodes.forEach(n => {
      if (n.data.type === 'prerequisite') {
        const level = n.data.level || 0;
        if (!prereqsByLevel[level]) prereqsByLevel[level] = [];
        prereqsByLevel[level].push(n);
      }
    });

    // Layout subtopics vertically on the left with reduced spacing
    const subtopicStartX = 100;
    const subtopicStartY = 100;
    const subtopicSpacing = 100;

    subtopics.forEach((node, idx) => {
      node.position = {
        x: subtopicStartX,
        y: subtopicStartY + idx * subtopicSpacing
      };
    });

    // Layout prerequisites horizontally to the right
    Object.keys(prereqsByLevel).forEach(levelKey => {
      const level = parseInt(levelKey);
      const prereqs = prereqsByLevel[level];
      const xOffset = subtopicStartX + 600 + (level * 400);

      let currentY = subtopicStartY;
      prereqs.forEach((prereq) => {
        prereq.position = {
          x: xOffset,
          y: currentY
        };
        currentY += 100;
      });
    });

    return layoutNodes;
  };

  const buildFlow = () => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );

    if (!currentData || !currentData.topics) {
      setNodes([]);
      setEdges([]);
      setAllNodes([]);
      setAllEdges([]);
      return;
    }

    const newNodes = [];
    const newEdges = [];
    const topics = currentData.topics;

    // Assign colors to topics
    const topicColorMap = {};
    topics.forEach((topic, idx) => {
      topicColorMap[idx] = topicColors[idx % topicColors.length];
    });

    // Build subtopic to parent topics mapping
    const subtopicParents = {};
    const subtopicData = {};

    topics.forEach((topic, topicIdx) => {
      topic.subtopics?.forEach((st) => {
        if (!subtopicParents[st.subtopic]) {
          subtopicParents[st.subtopic] = [];
          subtopicData[st.subtopic] = st;
        }
        subtopicParents[st.subtopic].push(topicIdx);
      });
    });

    // Calculate positions
    const totalTopics = topics.length;
    const totalHeight = (totalTopics - 1) * VERTICAL_GAP;
    const startY = Math.max(50, 400 - totalHeight / 2);

    // Get all unique subtopics and sort by parent topics
    const subtopicList = Object.keys(subtopicParents).sort((a, b) => {
      const aFirstParent = subtopicParents[a][0];
      const bFirstParent = subtopicParents[b][0];
      return aFirstParent - bFirstParent;
    });

    let currentY = startY;
    const subtopicYPositions = {};

    subtopicList.forEach((subtopicName) => {
      const label = subtopicName;
      const heightNeeded = estimateHeight(label);
      subtopicYPositions[subtopicName] = currentY;
      currentY += heightNeeded + 80;
    });

    // CREATE SUBTOPIC NODES
    subtopicList.forEach((subtopicName) => {
      const color = getSubtopicColor(subtopicName, topicColorMap, subtopicParents);
      const nodeId = `subtopic_${subtopicName.replace(/\s+/g, '_')}`;
      const parentTopicIndex = subtopicParents[subtopicName][0];
      const parentTopicName = topics[parentTopicIndex].topic;
      newNodes.push({
        id: nodeId,
        data: {
          label: `${subtopicName} (${parentTopicName})`,
          type: 'subtopic',
          fullData: subtopicData[subtopicName],
          parents: subtopicParents[subtopicName],
        },
        position: { x: 100 + LEVEL_GAP_X, y: subtopicYPositions[subtopicName] },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
          border: `3px solid ${color}`,
          borderRadius: '16px',
          padding: '14px 20px',
          fontSize: isMobile ? '11px' : '13px',
          fontWeight: '600',
          color: '#ffffff',
          cursor: 'pointer',
          boxShadow: `0 4px 16px ${color}66`,
          minWidth: isMobile ? '120px' : '160px',
          textAlign: 'center',
          transition: 'all 0.3s ease',
        },
      });

      // CREATE EDGES FROM TOPICS TO SUBTOPICS
      subtopicParents[subtopicName].forEach((topicIdx) => {
        const edgeColor = topicColorMap[topicIdx];
        newEdges.push({
          id: `topic_${topicIdx}-${nodeId}`,
          source: `topic_${topicIdx}`,
          target: nodeId,
          animated: false,
          style: {
            stroke: edgeColor,
            strokeWidth: 2,
          },
          type: 'default',
        });
      });
    });

    // FLATTEN ALL PREREQUISITES
    const allPrerequisitesMap = new Map();
    const prerequisiteRelationships = new Map();

    Object.entries(subtopicData).forEach(([subtopicName, data]) => {
      if (data.prerequisites && Array.isArray(data.prerequisites)) {
        data.prerequisites.forEach(prereq => {
          const flattened = flattenPrerequisites(prereq);
          flattened.forEach(flatPrereq => {
            if (!allPrerequisitesMap.has(flatPrereq.id)) {
              allPrerequisitesMap.set(flatPrereq.id, {
                ...flatPrereq,
                connectedSubtopics: new Set()
              });
            }
            if (flatPrereq.level === 0) {
              allPrerequisitesMap.get(flatPrereq.id).connectedSubtopics.add(subtopicName);
            }

            if (flatPrereq.parentId) {
              if (!prerequisiteRelationships.has(flatPrereq.parentId)) {
                prerequisiteRelationships.set(flatPrereq.parentId, []);
              }
              prerequisiteRelationships.get(flatPrereq.parentId).push(flatPrereq.id);
            }
          });
        });
      }
    });

    let levelTracker = {};
    const levelSpacing = 100;
    const levelXOffset = 400;
    const prerequisitePositions = new Map();

    const sortedPrereqs = Array.from(allPrerequisitesMap.entries())
      .sort((a, b) => {
        if (a[1].level !== b[1].level) return a[1].level - b[1].level;
        return 0;
      });

    sortedPrereqs.forEach(([prereqId, prereqData]) => {
      const baseX = 100 + 2 * LEVEL_GAP_X;
      const xPos = baseX + (prereqData.level * levelXOffset);

      let siblingCount = prerequisiteRelationships.get(prereqData.parentId)?.length || 1;
      let dynamicGap = 100 + (siblingCount * 30);

      if (levelTracker[prereqData.level] == null) {
        levelTracker[prereqData.level] = startY;
      }
      let yPos = levelTracker[prereqData.level];
      levelTracker[prereqData.level] += dynamicGap;

      prerequisitePositions.set(prereqId, { x: xPos, y: yPos });

      let prereqColor = '#4ade80';
      const connectedSubtopics = Array.from(prereqData.connectedSubtopics);
      if (connectedSubtopics.length > 0) {
        prereqColor = getSubtopicColor(connectedSubtopics[0], topicColorMap, subtopicParents);
      }

      const opacity = Math.max(0.3, 1 - (prereqData.level * 0.15));
      const borderWidth = Math.max(1, 2 - prereqData.level);

      newNodes.push({
        id: `prereq_${prereqId}`,
        data: {
          label: prereqData.name,
          type: 'prerequisite',
          level: prereqData.level,
          prerequisiteData: prereqData,
        },
        position: { x: xPos, y: yPos },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: `linear-gradient(135deg, ${prereqColor}${Math.round(opacity * 100).toString(16).padStart(2, '0')} 0%, ${prereqColor}${Math.round(opacity * 50).toString(16).padStart(2, '0')} 100%)`,
          border: `${borderWidth}px solid ${prereqColor}`,
          borderRadius: '12px',
          padding: prereqData.level === 0 ? (isMobile ? '10px 14px' : '12px 18px') : (isMobile ? '8px 12px' : '10px 14px'),
          fontSize: isMobile ? '10px' : (prereqData.level === 0 ? '12px' : '11px'),
          fontWeight: '600',
          color: prereqData.level === 0 ? '#1a1a1a' : '#4a5568',
          cursor: 'pointer',
          boxShadow: `0 4px 12px ${prereqColor}${Math.round(opacity * 64).toString(16).padStart(2, '0')}`,
          minWidth: isMobile ? (prereqData.level === 0 ? '100px' : '90px') : (prereqData.level === 0 ? '140px' : '120px'),
          textAlign: 'center',
        },
      });

      if (prereqData.level === 0) {
        connectedSubtopics.forEach(subtopicName => {
          const subtopicId = `subtopic_${subtopicName.replace(/\s+/g, '_')}`;
          const edgeColor = getSubtopicColor(subtopicName, topicColorMap, subtopicParents);

          newEdges.push({
            id: `${subtopicId}-prereq_${prereqId}`,
            source: subtopicId,
            target: `prereq_${prereqId}`,
            style: {
              stroke: edgeColor,
              strokeWidth: 2,
              strokeDasharray: '5,5',
            },
            type: 'default',
          });
        });
      }
    });

    prerequisiteRelationships.forEach((childIds, parentId) => {
      childIds.forEach(childId => {
        const parentData = allPrerequisitesMap.get(parentId);
        let edgeColor = '#94a3b8';

        if (parentData && parentData.connectedSubtopics.size > 0) {
          const firstSubtopic = Array.from(parentData.connectedSubtopics)[0];
          edgeColor = getSubtopicColor(firstSubtopic, topicColorMap, subtopicParents);
        }

        newEdges.push({
          id: `prereq_${parentId}-prereq_${childId}`,
          source: `prereq_${parentId}`,
          target: `prereq_${childId}`,
          style: {
            stroke: edgeColor,
            strokeWidth: 1.5,
            strokeDasharray: '3,3',
          },
          type: 'default',
        });
      });
    });

    setAllNodes(newNodes);
    setAllEdges(newEdges);
    setNodes(newNodes);
    setEdges(newEdges);
    setShowFullView(true);
    setSelectedNode(null);

    setTimeout(() => fitView({ padding: 0.3, duration: 400 }), 100);
  };

  const handleNodeClick = (event, node) => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );

    if (!currentData) return;

    if (node.data.type === 'subtopic') {
      setSelectedNode(node.id);
      setShowFullView(false);

      const parentIndices = node.data.parents || [];
      const subtopicName = node.data.label.replace(/ \(.*\)$/, '');

      const subtopicDataMap = {};
      currentData.topics.forEach((topic) => {
        topic.subtopics?.forEach((st) => {
          subtopicDataMap[st.subtopic] = st;
        });
      });

      const currentSubtopicData = subtopicDataMap[subtopicName];

      const allPrerequisiteIds = new Set();

      if (currentSubtopicData?.prerequisites) {
        const collectPrerequisiteIds = (prereqList) => {
          prereqList.forEach(prereq => {
            if (typeof prereq === 'object' && prereq.name) {
              const flattened = flattenPrerequisites(prereq);
              flattened.forEach(flatPrereq => {
                allPrerequisiteIds.add(flatPrereq.id);
              });
            }
          });
        };

        collectPrerequisiteIds(currentSubtopicData.prerequisites);
      }

      allEdges.forEach(edge => {
        if (edge.source === node.id && edge.target.startsWith('prereq_')) {
          const prereqId = edge.target.replace('prereq_', '');
          allPrerequisiteIds.add(prereqId);
        }
      });

      const filteredNodes = allNodes.filter((n) => {
        if (n.id === node.id) return true;
        if (n.data.type === 'topic' && parentIndices.includes(n.data.index)) return true;
        if (n.data.type === 'prerequisite') {
          const prereqId = n.id.replace('prereq_', '');
          return allPrerequisiteIds.has(prereqId);
        }
        return false;
      });

      const visibleNodeIds = new Set(filteredNodes.map((n) => n.id));
      const filteredEdges = allEdges.filter(
        (edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
      );

      const relayoutedNodes = relayoutFilteredNodes(filteredNodes, node);

      setNodes(relayoutedNodes);
      setEdges(filteredEdges);

      const prereqNames = [];
      const allPrereqDetails = [];

      if (currentSubtopicData?.prerequisites) {
        currentSubtopicData.prerequisites.forEach(prereq => {
          if (typeof prereq === 'object' && prereq.name) {
            const flattened = flattenPrerequisites(prereq);
            prereqNames.push(...flattened.map(p => `${'  '.repeat(p.level)}${p.name}`));
            allPrereqDetails.push(...flattened);
          } else if (typeof prereq === 'string') {
            prereqNames.push(prereq);
          }
        });
      }

      setSidePanelContent({
        type: 'subtopic',
        subtopic: node.data.label,
        prerequisites: prereqNames,
        allPrerequisites: allPrereqDetails,
        parents: parentIndices.map((idx) => currentData.topics[idx].topic),
        subtopicData: currentSubtopicData,
      });

      // Open detail drawer on mobile
      if (isMobile || isTablet) {
        setDetailDrawerOpen(true);
      }

      setTimeout(() => fitView({ padding: 0.5, duration: 400 }), 100);

    } else if (node.data.type === 'topic') {
      const topic = node.data.topicData;
      setSidePanelContent({
        type: 'topic',
        topic: topic.topic,
        topic_prerequisites: topic.topic_prerequisites || [],
        description: topic.description || '',
        learning_material: topic.learning_material || [],
      });

      if (isMobile || isTablet) {
        setDetailDrawerOpen(true);
      }
    } else if (node.data.type === 'prerequisite') {
      setSidePanelContent({
        type: 'prerequisite',
        prerequisite: node.data.label,
        level: node.data.level || 0,
        prerequisiteData: node.data.prerequisiteData,
      });

      if (isMobile || isTablet) {
        setDetailDrawerOpen(true);
      }
    }
  };

  const handleViewComplete = () => {
    const relayoutedAllNodes = relayoutAllNodes(allNodes);
    setNodes(relayoutedAllNodes);
    setEdges(allEdges);
    setShowFullView(true);
    setSelectedNode(null);
    setSidePanelContent(null);
    setTimeout(() => fitView({ padding: 0.3, duration: 400 }), 100);
  };

  const handleReset = () => {
    if (selectedClass && selectedSubject) {
      buildFlow();
    }
  };

  const exportToPdf = async () => {
    await exportFlowToPdf({
      nodes,
      reactFlowWrapper,
      fileName: `knowledge-map-${selectedClass}-${selectedSubject}-${new Date().toISOString().split('T')[0]}.pdf`,
      backgroundColor: '#ffffff',
      rowsPerPage: 4,
      rowGap: 150,
      padding: 100,
      minHeight: 400,
      pixelRatio: 2.5,
      onProgress: (message, type) => showSnackBar(message, type),
      onError: (message, type) => showSnackBar(message, type),
      onSuccess: (message, type) => showSnackBar(message, type),
    });
    handleDownloadClose();
  };

  const exportAsTablePdf = () => {
    const currentData = curriculumData.find(
      (c) => c.class === selectedClass && c.subject === selectedSubject
    );
    exportTableToPdf({
      data: currentData,
      fileName: `knowledge-map-table-${selectedClass}-${selectedSubject}-${new Date().toISOString().split('T')[0]}.pdf`,
      onProgress: (message, type) => showSnackBar(message, type),
      onError: (message, type) => showSnackBar(message, type),
      onSuccess: (message, type) => showSnackBar(message, type),
    });
    handleDownloadClose();
  };
  // Sidebar content component for reuse
  const SidebarContent = () => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* <Box sx={{ mb: 2 }}>
        <SearchableDropdown
          label="Select Class"
          options={availableClasses.map((cls) => ({
            label: `Class ${cls}`,
            value: cls,
          }))}
          value={selectedClass ? { label: `Class ${selectedClass}`, value: selectedClass } : null}
          onChange={(val) => {
            setSelectedClass(val?.value || '');
            setSelectedSubject('');
          }}
          size="small"
        />
      </Box> */}

       {hasClassListPermission ? (
        <Box sx={{ mb: 2 }}>
          <SearchableDropdown
            label="Select Class"
            options={availableClasses.map((cls) => ({
              label: `Class ${cls}`,
              value: cls,
            }))}
            value={selectedClass ? { label: `Class ${selectedClass}`, value: selectedClass } : null}
            onChange={(val) => {
              setSelectedClass(val?.value || '');
              setSelectedSubject('');
            }}
            size="small"
          />
        </Box>
      ) : (
        selectedClass && (
          <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: '#f0f4ff', borderRadius: 1, border: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 13, color: '#4338ca', fontWeight: 600 }}>
              📚 Class {selectedClass}
            </Typography>
          </Box>
        )
      )}

      <Box sx={{ mb: 2 }}>
        <SearchableDropdown
          label="Select Subject"
          options={availableSubjects.map((sub) => ({
            label: sub,
            value: sub,
          }))}
          value={selectedSubject ? { label: selectedSubject, value: selectedSubject } : null}
          onChange={(val) => setSelectedSubject(val?.value || '')}
          disabled={!selectedClass}
          size="small"
        />
      </Box>

      {constantActions.userHasPermission([globalpermissions?.performancePermissions?.knowledgeMap]) && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/r/student/create')}
            size={isMobile ? 'small' : 'medium'}
          >
            Create Knowledge Map
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {nodes.length > 0 && (
          <>
            {!showFullView && (
              <Tooltip title="View Complete Map">
                <Button
                  onClick={handleViewComplete}
                  variant="contained"
                  size="small"
                  startIcon={<Visibility />}
                  sx={{ flexGrow: 1 }}
                >
                  {isMobile ? 'Full' : 'View Complete'}
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Reset View">
              <IconButton onClick={handleReset} color="primary" size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export as PDF">
              <IconButton onClick={exportToPdf} color="primary" size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit/Delete">
              <IconButton
                onClick={(e) => handleMenuOpen(e, 'current')}
                color="primary"
                size="small"
              >
                ⋮
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {sidePanelContent && (
        <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: '1px solid #e2e8f0', flexGrow: 1, overflowY: 'auto' }}>
          {sidePanelContent.type === 'topic' && (
            <>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                📚 {sidePanelContent.topic}
              </Typography>
              <Divider sx={{ my: 2 }} />

              {sidePanelContent.description && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                    ℹ️ Description
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2, fontSize: isMobile ? '0.875rem' : '1rem' }}>
                    {sidePanelContent.description}
                  </Typography>
                </>
              )}

              {sidePanelContent.prerequisites?.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mt: 2, mb: 1 }}>
                    ✅ Prerequisites
                  </Typography>
                  <List dense>
                    {sidePanelContent.prerequisites.map((p, i) => (
                      <ListItem key={i} sx={{ pl: 0 }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                          • {p}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </>
          )}

          {sidePanelContent.type === 'prerequisite' && (
            <>
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, color: '#475569', mb: 2 }}>
                ✅ {sidePanelContent.prerequisite}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: isMobile ? '0.875rem' : '1rem' }}>
                This is a prerequisite concept required for understanding related subtopics.
              </Typography>
            </>
          )}
        </Paper>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: { sm: 'row', xs: 'column' }, height: { sm: '90vh', xs: '100%' }, bgcolor: '#f8fafc' }}>
      <Box ref={reactFlowWrapper} sx={{
        width: { sm: '70%', xs: '100%' },
        height: { sm: '100%', xs: '60vh' },   // ⭐ IMPORTANT
        position: 'relative',
      }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodeClick={handleNodeClick}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          fitView={false}
          style={{ background: '#f8fafc' }}
          minZoom={useCompactZoom ? 0.1 : 0.01}
          maxZoom={useCompactZoom ? 2 : 8}
        >
          <Controls
            style={{ button: { background: 'white', borderColor: '#e2e8f0' } }}
            position={isMobile ? 'bottom-right' : 'top-right'}
            showInteractive={!isMobile}
          />
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#cbd5e1" />
        </ReactFlow>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: 150,
            },
          }}
        >
          {constantActions.userHasPermission([globalpermissions?.performancePermissions?.knowledgeMap]) && (
            <MenuItem onClick={handleEdit} sx={{ gap: 1, py: 1.5 }}>
              ✏️ Edit
            </MenuItem>
          )}
          {constantActions.userHasPermission([globalpermissions?.performancePermissions?.deleteKnowledgeMap]) && (
            <MenuItem onClick={handleDelete} sx={{ gap: 1, py: 1.5, color: 'error.main' }}>
              🗑️ Delete
            </MenuItem>
          )}
        </Menu>

        <Menu
          anchorEl={anchorEl1}
          open={Boolean(anchorEl1)}
          onClose={handleDownloadClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: 150,
            },
          }}
        >
          <MenuItem onClick={exportToPdf} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            Export graph as pdf
          </MenuItem>

          <MenuItem onClick={exportAsTablePdf} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <TableChartIcon fontSize="small" />
            </ListItemIcon>
            Export table as pdf
          </MenuItem>
        </Menu>

      </Box>

      <Box sx={{ width: { sm: '30%', xs: '100%' }, overflowY: 'auto', p: { sm: 2, xs: 0 }, bgcolor: '#ffffff' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {nodes.length > 0 && (
            <>
              {!showFullView && (
                <Tooltip title="View Complete Map">
                  <Button
                    onClick={handleViewComplete}
                    variant="contained"
                    size="small"
                    startIcon={<Visibility />}
                    sx={{ flexGrow: 1 }}
                  >
                    View Complete
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Reset View">
                <IconButton onClick={handleReset} color="primary" size="small">
                  <Refresh />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title="Export as PDF">
                <IconButton onClick={exportToPdf} color="primary" size="small">
                  <DownloadIcon />
                </IconButton>
              </Tooltip> */}
              <Tooltip title="Export as PDF">
                <IconButton onClick={(e) => handleDownloadOpen(e, 'current')} color="primary" size="small">
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              {/* <Tooltip title="Export Table as PDF">
                <IconButton onClick={exportAsTablePdf} color="primary" size="small">
                  <TableViewIcon />
                </IconButton>
              </Tooltip> */}
              <Tooltip title="Edit/Delete">
                <IconButton
                  onClick={(e) => handleMenuOpen(e, 'current')}
                  color="primary"
                  size="small"
                >
                  ⋮
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
         {hasClassListPermission   && (
          <OrganisationDropdown
            value={selectedOrganisation}
            onChange={(val) => {
              setSelectedOrganisation(val);
              setSelectedClass('');
              setSelectedSubject('');
            }}
          />
        )}
        {hasClassListPermission ? (
          <Box sx={{ mb: 2 }}>
            <SearchableDropdown
              label="Select Class"
              options={availableClasses.map((cls) => ({
                label: `Class ${cls}`,
                value: cls,
              }))}
              value={selectedClass ? { label: `Class ${selectedClass}`, value: selectedClass } : null}
              onChange={(val) => {
                setSelectedClass(val?.value || '');
                setSelectedSubject('');
              }}
              size="small"
            />
          </Box>
        ) : (
          selectedClass && (
            <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: '#f0f4ff', borderRadius: 1, border: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 13, color: '#4338ca', fontWeight: 600 }}>
                📚 Class {selectedClass}
              </Typography>
            </Box>
          )
        )}
        <Box sx={{ mb: 2 }}>
          <SearchableDropdown
            label="Select Subject"
            options={availableSubjects.map((sub) => ({
              label: sub,
              value: sub,
            }))}
            value={selectedSubject ? { label: selectedSubject, value: selectedSubject } : null}
            onChange={(val) => setSelectedSubject(val?.value || '')}
            disabled={!selectedClass}
            size="small"
          />
        </Box>
        {constantActions.userHasPermission([globalpermissions?.performancePermissions?.knowledgeMap]) && (
          <Box sx={{ mb: { sm: 3, xs: 1 } }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/r/student/create')}
            >
              Create Knowledge Map
            </Button>
          </Box>
        )}
        {sidePanelContent && (
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
            {sidePanelContent.type === 'topic' && (
              <>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                  📚 {sidePanelContent.topic}
                </Typography>
                <Divider sx={{ my: 2 }} />

                {sidePanelContent.description && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
                      ℹ️ Description
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                      {sidePanelContent.description}
                    </Typography>
                  </>
                )}

                {sidePanelContent.prerequisites?.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', mt: 2, mb: 1 }}>
                      ✅ Prerequisites
                    </Typography>
                    <List dense>
                      {sidePanelContent.prerequisites.map((p, i) => (
                        <ListItem key={i} sx={{ pl: 0 }}>
                          <Typography variant="body2" sx={{ color: '#475569' }}>
                            • {p}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </>
            )}

            {sidePanelContent.type === 'prerequisite' && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#475569', mb: 2 }}>
                  ✅ {sidePanelContent.prerequisite}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  This is a prerequisite concept required for understanding related subtopics.
                </Typography>
              </>
            )}
          </Paper>
        )}
      </Box>
      {confirmOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 2,
          }}
          onClick={() => setConfirmOpen(false)}
        >
          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              maxWidth: { xs: '90%', sm: 400 },
              width: '100%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" sx={{ mb: 2, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
              Delete Knowledge Map
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Are you sure you want to delete this knowledge map?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button onClick={() => handleConfirmDelete(false)} size={isMobile ? 'small' : 'medium'}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleConfirmDelete(true)}
                size={isMobile ? 'small' : 'medium'}
              >
                Delete
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '1200px',
          zIndex: -1,
          opacity: 0,
          pointerEvents: 'none',
          backgroundColor: '#fff'
        }}
      >
        <Box ref={tableRef}>
          <KnowledgeMapTable
            data={curriculumData.find(
              (c) => c.class === selectedClass && c.subject === selectedSubject
            )}
            subject={selectedSubject}
            className={selectedClass}
            topicColors={topicColors}
            forPdf={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default function DynamicFlow() {
  return (
    <ReactFlowProvider>
      <FlowComponent />
    </ReactFlowProvider>
  );
}
